'use client';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { UploadCloud, X } from 'lucide-react';
import Image from 'next/image';
import { useModelTraining } from '.';
import { ALLOWED_IMAGE_TYPES } from '@/lib/constants';
import { useMemo, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function TrainingImagesSection() {
  const { trainingImages, setTrainingImages } = useModelTraining();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cache blob URLs стабилно - не пресъздаваме съществуващите
  const urlMap = useMemo(() => {
    const existingUrls = new Map();

    return trainingImages.map((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;

      // Ако вече имаме URL за този файл, използваме го
      if (!existingUrls.has(key)) {
        existingUrls.set(key, URL.createObjectURL(file));
      }

      return {
        key,
        url: existingUrls.get(key),
      };
    });
  }, [trainingImages]);

  // Revoke blob URLs on cleanup
  const previousUrlsRef = useRef<string[]>([]);
  useEffect(() => {
    // Cleanup само URL-тата които вече не се използват
    const currentUrls = urlMap.map((entry) => entry.url);
    const urlsToRevoke = previousUrlsRef.current.filter(
      (url) => !currentUrls.includes(url),
    );

    urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    previousUrlsRef.current = currentUrls;
  }, [urlMap]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    const newValidFiles = files
      .filter((file) => ALLOWED_IMAGE_TYPES.includes(file.type))
      .filter(
        (file) =>
          !trainingImages.some(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified,
          ),
      );

    if (newValidFiles.length) {
      setTrainingImages([...trainingImages, ...newValidFiles]);
    }

    // Reset input value за да може да качиш същия файл отново
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (index: number) => {
    setTrainingImages(trainingImages.filter((_, i) => i !== index));
  };

  const hasImages = trainingImages.length > 0;

  return (
    <div className='px-4 space-y-2'>
      {/* Hidden file input - по-чист подход */}
      <input
        ref={fileInputRef}
        type='file'
        accept={ALLOWED_IMAGE_TYPES.join(',')}
        multiple
        onChange={handleFileChange}
        className='hidden'
      />

      <Label className='text-base font-semibold text-neutral-50 leading-tight'>
        Training Images
      </Label>

      <Label className='text-xs text-stone-50/60 flex items-center gap-2'>
        <Check
          className={cn(hasImages ? 'text-fuchsia-600' : 'text-stone-50/60')}
        />
        Upload at least 10–20 images
      </Label>

      <div
        className={`rounded-xl p-4 space-y-4 ${
          hasImages
            ? 'border border-zinc-700 border-dashed bg-neutral-900'
            : 'border-2 border-dashed border-fuchsia-600'
        }`}
      >
        {/* Images grid */}
        {hasImages && (
          <>
            <div className='grid justify-center grid-cols-[repeat(auto-fill,_minmax(80px,_80px))] gap-3'>
              {urlMap.map(({ key, url }, idx) => (
                <div
                  key={key}
                  className='relative w-20 h-20 overflow-hidden rounded-md bg-neutral-800'
                >
                  <Image
                    src={url}
                    alt={`uploaded-${idx}`}
                    fill
                    className='object-cover'
                  />
                  <Button
                    onMouseDown={() => handleRemove(idx)}
                    className='absolute top-1 right-1 bg-black/60 hover:bg-black/80 p-[2px] rounded-full'
                    size='icon'
                  >
                    <X className='w-2 h-2 text-white' />
                  </Button>
                </div>
              ))}
            </div>

            <div className='pt-4 flex justify-center'>
              <Button
                variant='outline'
                className='rounded-full border-fuchsia-600 text-stone-50/90 hover:bg-fuchsia-500/10 text-sm px-4 py-1.5'
                onMouseDown={handleUpload}
              >
                + Add More Images
              </Button>
            </div>
          </>
        )}

        {!hasImages && (
          <div className='flex flex-col items-center justify-center text-center space-y-4'>
            <UploadCloud className='w-8 h-14 text-fuchsia-500' />
            <Button
              variant='outline'
              className='rounded-full border-fuchsia-600 text-stone-50/90 hover:bg-fuchsia-500/10 focus-visible:outline-none focus-visible:ring-0'
              onMouseDown={handleUpload}
            >
              Upload Images
            </Button>
            <p className='text-xs text-stone-50/90/60'>
              Supports JPEG and PNG up to 20MB.
            </p>
          </div>
        )}

        <p className='text-right text-xs text-stone-50/60'>
          {trainingImages.length} / 100 images
        </p>
      </div>
    </div>
  );
}
